import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { mutate } from '@/runtime'
import type { Resource } from '@/db/types'
import { usePagesFor } from '@/db/hooks'
import { WS } from '@/db/ops'
import { KindError } from '@/kinds/kind'
import { Button, Field, Sheet, Tag, cx } from '@/components/ui'
import { IconButtonField } from '@/components/EmojiPicker'
import { FileText } from 'lucide-react'
import { Icon, iconText } from '@/lib/icons'
import { useScope } from '@/state/scope'
import { ALL } from '@/kinds/workspace/schema'
import { nextRank, type PageSpec, type PageStatus } from './schema'
import type { PageResource } from './apply'

type Props = { resource: Resource<PageSpec, PageStatus> }

export const PAGE_ICON = '▫'
/** Text glyph for places that can only show a string (command palette hints). */
export const pageIcon = (p: PageResource) => iconText(p.spec.icon, PAGE_ICON)

/** The page's emoji, or a document icon when it has none. */
export const PageGlyph = ({
  page,
  size = 16,
  className,
}: {
  page: { spec: { icon?: string } }
  size?: number
  className?: string
}) =>
  page.spec.icon ? (
    <Icon value={page.spec.icon} size={size} className={className} />
  ) : (
    <FileText size={size} className={cx('text-muted', className)} aria-hidden />
  )

/** Pinned first, then rank, then most recently touched. */
export const sortPages = (pages: PageResource[]): PageResource[] =>
  [...pages].sort((a, b) => {
    if (a.spec.pinned !== b.spec.pinned) return a.spec.pinned ? -1 : 1
    const ra = a.spec.rank ?? ''
    const rb = b.spec.rank ?? ''
    if (ra !== rb) {
      if (!ra) return 1
      if (!rb) return -1
      return ra < rb ? -1 : 1
    }
    return a.status.last_touched < b.status.last_touched ? 1 : -1
  })

/** Create a page in a workspace, optionally under a parent (project, task or page). */
export const createPage = async (
  ws: string | undefined,
  parentId?: string,
  siblings: PageResource[] = [],
  spec: Partial<PageSpec> = {},
) => {
  const labels: Record<string, string> = ws && ws !== ALL ? { [WS]: ws } : {}
  const r = (await mutate.create(
    'Page',
    {
      title: '',
      blocks: [],
      text: '',
      pinned: false,
      rank: nextRank(siblings.map((s) => s.spec.rank)),
      ...spec,
    },
    labels,
  )) as PageResource
  // belongs_to is the same edge for tasks→projects and pages→anything
  if (parentId) await mutate.setProject(r.id, parentId)
  return r
}

/** A page in a list: icon, name, pinned tag; links to the page. */
export const PageRow = ({
  resource,
  hint,
  depth = 0,
}: Props & { hint?: React.ReactNode; depth?: number }) => (
  <li>
    <Link
      to="/pages/$id"
      params={{ id: resource.id }}
      className="flex items-center gap-2.5 hover:bg-raised/50 px-3.5 py-2 min-h-12"
      style={depth ? { marginLeft: `${depth * 1.25}rem` } : undefined}
      data-page-id={resource.id}
    >
      <span className="w-6 flex items-center justify-center shrink-0 text-base">
        <PageGlyph page={resource} />
      </span>
      <span className="flex-1 truncate font-medium">{resource.name}</span>
      {resource.spec.pinned && <Tag tone="accent">pinned</Tag>}
      {hint}
    </Link>
  </li>
)

/** Pages attached to a project, task or page, with a one-tap "New page". */
export const PagesSection = ({
  parentId,
  ws,
  title = 'Pages',
  className = 'mt-6',
  chipOnly,
}: {
  parentId: string
  ws?: string
  title?: string
  className?: string
  /** only the add button, as a chip among others (a task with no pages yet) */
  chipOnly?: boolean
}) => {
  const navigate = useNavigate()
  const pages = usePagesFor(parentId)
  const add = async () => {
    const r = await createPage(ws, parentId, pages)
    void navigate({ to: '/pages/$id', params: { id: r.id } })
  }
  if (chipOnly)
    return (
      <Button variant="chip" onClick={() => void add()} data-testid="page-add">
        Add a page
      </Button>
    )
  return (
    <section className={className} data-testid="pages-section">
      <div className="flex items-center justify-between mb-1.5">
        <h2
          className={className === 'mt-6' ? 'text-[13px] font-semibold text-muted' : 'text-sm font-semibold'}
        >
          {title}
        </h2>
        <Button variant="chip" onClick={() => void add()} data-testid="page-add">
          New page
        </Button>
      </div>
      {/* empty says nothing: the heading and its "New page" are the whole story, and every task page has one */}
      {pages.length > 0 && (
        <ul className="group-list">
          {sortPages(pages).map((p) => (
            <PageRow key={p.id} resource={p} />
          ))}
        </ul>
      )}
    </section>
  )
}

/** Read-only projection (used by generic detail surfaces); the real thing is the editor route. */
export const PageDetail = ({ resource }: Props) => (
  <article>
    <p className="text-sm text-fg whitespace-pre-wrap leading-6">{resource.spec.text || 'Empty page.'}</p>
  </article>
)

const formSchema = z.object({
  title: z.string().max(200),
  icon: z.string().max(40).optional(),
  pinned: z.boolean(),
})
type FormValues = z.output<typeof formSchema>

/** Rename, icon and pin. Without a resource it creates an empty page in the current scope. */
export const PageForm = ({ resource, onDone }: { resource?: PageResource; onDone: () => void }) => {
  const [error, setError] = useState<string | null>(null)
  const scope = useScope((s) => s.ws)
  const navigate = useNavigate()
  const { register, handleSubmit, control, formState } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: resource
      ? { title: resource.spec.title, icon: resource.spec.icon, pinned: resource.spec.pinned }
      : { title: '', pinned: false },
  })
  const submit = async (v: FormValues) => {
    setError(null)
    try {
      if (resource) {
        await mutate.patch(resource.id, {
          spec: { title: v.title.trim(), icon: v.icon ?? null, pinned: v.pinned },
        })
      } else {
        const r = await createPage(scope, undefined, [], {
          title: v.title.trim(),
          icon: v.icon,
          pinned: v.pinned,
        })
        void navigate({ to: '/pages/$id', params: { id: r.id } })
      }
      onDone()
    } catch (e) {
      setError(e instanceof KindError ? e.message : 'Could not save')
    }
  }
  return (
    <Sheet open onClose={onDone} title={resource ? 'Page settings' : 'New page'}>
      <form onSubmit={(e) => void handleSubmit(submit)(e)}>
        <Field label="Title" error={formState.errors.title?.message}>
          <input {...register('title')} autoFocus placeholder="Untitled" data-testid="page-title-input" />
        </Field>
        <Controller
          control={control}
          name="icon"
          render={({ field }) => (
            <IconButtonField value={field.value ?? undefined} onChange={field.onChange} />
          )}
        />
        <label className="flex items-center gap-2 text-sm mb-4">
          <input type="checkbox" {...register('pinned')} className="h-4 w-4" /> Pinned to the sidebar
        </label>
        {error && <p className="text-xs text-rose-300 mb-2">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button onClick={onDone}>Cancel</Button>
          <Button variant="primary" type="submit" data-testid="page-save">
            Save
          </Button>
        </div>
      </form>
    </Sheet>
  )
}
